import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { ArrowLeft, Ticket, Eye, Trash2, Copy, Package } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import CouponDetailModal from "@/components/admin/CouponDetailModal";

interface Coupon {
  id: string;
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
  current_uses: number;
  max_uses: number | null;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applicable_plans: string[];
  stripe_coupon_id: string | null;
  duration_type: string;
  duration_in_months: number | null;
}

interface FriendStats {
  directUses: number;
  replacedButConverted: number;
  totalAffiliateConversions: number;
}

type CouponFilterTab = 'all' | 'valid' | 'expired';

export default function AdminCoupons() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [friendStats, setFriendStats] = useState<FriendStats | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CouponFilterTab>('all');
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: 0,
    max_uses: null as number | null,
    valid_from: "",
    valid_until: "",
    applicable_plans: [] as string[],
    duration_type: "once",
    duration_in_months: null as number | null,
  });

  // Filter coupons based on active tab
  const filteredCoupons = useMemo(() => {
    const now = new Date();
    
    return coupons.filter(coupon => {
      const isExpired = coupon.valid_until && new Date(coupon.valid_until) < now;
      const isMaxUsesReached = coupon.max_uses !== null && coupon.current_uses >= coupon.max_uses;
      const isInactive = !coupon.is_active;
      const isTerminatedOrExpired = isExpired || isMaxUsesReached || isInactive;
      
      switch (activeTab) {
        case 'valid':
          return !isTerminatedOrExpired;
        case 'expired':
          return isTerminatedOrExpired;
        default:
          return true;
      }
    });
  }, [coupons, activeTab]);

  useEffect(() => {
    loadCoupons();
    loadFriendStats();
  }, []);

  const loadFriendStats = async () => {
    try {
      // Total affiliate conversions
      const { count: totalConversions } = await supabase
        .from('affiliate_conversions')
        .select('*', { count: 'exact', head: true });

      // Conversions with FRIEND coupon
      const { count: friendUses } = await supabase
        .from('affiliate_conversions')
        .select('*', { count: 'exact', head: true })
        .eq('coupon_used', 'FRIEND');

      // Conversions with another coupon (FRIEND was replaced)
      const { count: replacedCount } = await supabase
        .from('affiliate_conversions')
        .select('*', { count: 'exact', head: true })
        .not('coupon_used', 'is', null)
        .neq('coupon_used', 'FRIEND');

      setFriendStats({
        directUses: friendUses || 0,
        replacedButConverted: replacedCount || 0,
        totalAffiliateConversions: totalConversions || 0
      });
    } catch (error) {
      console.error("Error loading FRIEND stats:", error);
    }
  };

  const loadCoupons = async () => {
    try {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCoupons(data || []);
    } catch (error) {
      console.error("Error loading coupons:", error);
      toast({ title: t("admin.loadError"), variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Helper per determinare se ci sono subscription plans selezionate
  const hasSubscriptionPlans = () => {
    const subscriptionPlans = ['base', 'pro', 'plus'];
    // Se applicable_plans è vuoto, significa "tutti" quindi include subscription
    if (formData.applicable_plans.length === 0) return true;
    return formData.applicable_plans.some(plan => subscriptionPlans.includes(plan));
  };

  const createCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mappatura automatica basata sul tipo di prodotto
    let finalDurationType = formData.duration_type;
    let finalDurationMonths = formData.duration_in_months;

    // Se non ci sono subscription plans, forza "once" (one-time payment)
    if (!hasSubscriptionPlans()) {
      finalDurationType = 'once';
      finalDurationMonths = null;
    } else if (formData.duration_type === 'once') {
      // Per subscription: "once" diventa "repeating" con 3 mesi
      finalDurationType = 'repeating';
      finalDurationMonths = 3;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke("create-coupon-admin", {
        body: {
          code: formData.code || null, // Will be auto-generated if empty
          description: formData.description,
          discount_type: formData.discount_type,
          discount_value: formData.discount_value,
          max_uses: formData.max_uses,
          valid_from: formData.valid_from,
          valid_until: formData.valid_until,
          applicable_plans: formData.applicable_plans,
          duration_type: finalDurationType,
          duration_in_months: finalDurationMonths,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast({ title: data.message, variant: "success" });
        if (data.stripe_error) {
          toast({ title: `Stripe: ${data.stripe_error}`, variant: "warning" });
        }
        setShowCreateModal(false);
        loadCoupons();
        
        // Reset form
        setFormData({
          code: "",
          description: "",
          discount_type: "percentage",
          discount_value: 0,
          max_uses: null,
          valid_from: "",
          valid_until: "",
          applicable_plans: [],
          duration_type: "once",
          duration_in_months: null,
        });
      } else {
        throw new Error(data.error || "Failed to create coupon");
      }
    } catch (error: any) {
      console.error("Error creating coupon:", error);
      toast({ title: error.message || t("admin.couponCreateError"), variant: "error" });
    }
  };

  const toggleCoupon = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("coupons")
        .update({ is_active: isActive })
        .eq("id", id);

      if (error) throw error;

      toast({ title: t("admin.couponUpdated"), variant: "success" });
      loadCoupons();
    } catch (error) {
      console.error("Error toggling coupon:", error);
      toast({ title: t("admin.couponUpdateError"), variant: "error" });
    }
  };

  const handleDeleteClick = (id: string) => {
    setCouponToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!couponToDelete) return;

    try {
      const { data, error } = await supabase.functions.invoke("delete-coupon-admin", {
        body: { id: couponToDelete },
      });

      if (error) throw error;

      if (data.success) {
        toast({ title: data.message, variant: "success" });
        if (data.stripe_error) {
          toast({ title: `Stripe: ${data.stripe_error}`, variant: "warning" });
        }
        loadCoupons();
      } else {
        throw new Error(data.error || "Failed to delete coupon");
      }
    } catch (error: any) {
      console.error("Error deleting coupon:", error);
      toast({ title: error.message || t("admin.couponDeleteError"), variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setCouponToDelete(null);
    }
  };

  // Helper function to map plan IDs to translation keys
  const getPlanTranslationKey = (plan: string): string => {
    const keyMap: Record<string, string> = {
      'social_boost': 'socialBoost',
      'job_posting_single': 'jobSingle',
      'job_posting_pack_3': 'jobPack3',
      'job_posting_pack_5': 'jobPack5',
    };
    return keyMap[plan] || plan;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface rounded w-1/4"></div>
            <div className="h-96 bg-surface rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.coupons.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.coupons.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <Ticket className="w-10 h-10 text-purple-600" />
            {t("admin.coupons.title")}
          </h1>
        </div>

        {/* Action button */}
        <div className="mb-6">
          <BrutalButton
            onClick={() => setShowCreateModal(true)}
            variant="primary"
            className="flex items-center gap-2"
          >
            <Ticket className="w-5 h-5" />
            {t("admin.createNewCoupon")}
          </BrutalButton>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as CouponFilterTab)} className="mb-6">
          <TabsList className="border-[3px] border-dark">
            <TabsTrigger value="all" className="font-heading">
              {t("admin.coupons.tabs.all")} ({coupons.length})
            </TabsTrigger>
            <TabsTrigger value="valid" className="font-heading">
              {t("admin.coupons.tabs.valid")} ({coupons.filter(c => {
                const now = new Date();
                const isExpired = c.valid_until && new Date(c.valid_until) < now;
                const isMaxUsesReached = c.max_uses !== null && c.current_uses >= c.max_uses;
                return c.is_active && !isExpired && !isMaxUsesReached;
              }).length})
            </TabsTrigger>
            <TabsTrigger value="expired" className="font-heading">
              {t("admin.coupons.tabs.expired")} ({coupons.filter(c => {
                const now = new Date();
                const isExpired = c.valid_until && new Date(c.valid_until) < now;
                const isMaxUsesReached = c.max_uses !== null && c.current_uses >= c.max_uses;
                return !c.is_active || isExpired || isMaxUsesReached;
              }).length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Coupons table */}
        <BrutalCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b-[3px] border-dark">
                <tr>
                  <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.code")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">Link</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.discount")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.coupons.table.headerDuration")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.coupons.table.headerApplicability")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.uses")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.validity")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.coupons.table.headerStripe")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.active")}</th>
                  <th className="px-6 py-4 text-center font-heading font-bold">{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
              {filteredCoupons.map((coupon) => (
                <tr key={coupon.id} className="border-b-[3px] border-dark hover:bg-surface">
                  <td 
                    className="px-6 py-4 font-mono font-bold text-primary cursor-pointer hover:underline"
                    onClick={() => {
                      setSelectedCoupon(coupon);
                      setShowDetailModal(true);
                    }}
                  >
                    {coupon.code}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              const url = `https://inclusia.pro/pricing?coupon=${coupon.code}`;
                              navigator.clipboard.writeText(url);
                              toast({ title: "Link copiato!", description: url });
                            }}
                            className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                            aria-label="Copia link con coupon"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copia link con coupon</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="px-6 py-4 text-center font-bold">
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}%`
                      : coupon.discount_type === "fixed"
                      ? `€${coupon.discount_value}`
                      : t("admin.free")}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {coupon.duration_type === 'repeating' && coupon.duration_in_months === 3 ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {t("admin.coupons.table.badgeDurationOnce")}
                      </Badge>
                    ) : coupon.duration_type === 'forever' ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        {t("admin.coupons.table.badgeDurationForever")}
                      </Badge>
                    ) : coupon.duration_type === 'repeating' ? (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                        {t("admin.coupons.table.badgeDurationMonths", { months: coupon.duration_in_months })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {t("admin.coupons.table.badgeDurationPayment")}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {!coupon.applicable_plans || coupon.applicable_plans.length === 0 ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        {t("admin.coupons.applicability.all")}
                      </Badge>
                    ) : (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {coupon.applicable_plans.map(plan => (
                          <Badge key={plan} variant="secondary" className="text-xs">
                            {t(`admin.coupons.plans.${getPlanTranslationKey(plan)}`)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {coupon.code === 'FRIEND' && friendStats ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted decoration-muted-foreground">
                              {coupon.current_uses}/{coupon.max_uses || "∞"}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="p-3 max-w-xs">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-4">
                                <span>{t('admin.coupons.friend.directUses')}:</span>
                                <span className="font-bold">{friendStats.directUses}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span>{t('admin.coupons.friend.replacedConverted')}:</span>
                                <span className="font-bold">{friendStats.replacedButConverted}</span>
                              </div>
                              <Separator className="my-1" />
                              <div className="flex justify-between gap-4 text-primary font-semibold">
                                <span>{t('admin.coupons.friend.totalConversions')}:</span>
                                <span>{friendStats.totalAffiliateConversions}</span>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span>{coupon.current_uses}/{coupon.max_uses || "∞"}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center text-sm">
                    {coupon.valid_from ? format(new Date(coupon.valid_from), "dd/MM/yyyy") : "—"} -{" "}
                    {coupon.valid_until ? format(new Date(coupon.valid_until), "dd/MM/yyyy") : "∞"}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {coupon.stripe_coupon_id ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        {t("admin.coupons.table.badgeSync")}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-300">
                        {t("admin.coupons.table.badgeDbOnly")}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Switch
                      checked={coupon.is_active}
                      onCheckedChange={(checked) =>
                        toggleCoupon(coupon.id, checked)
                      }
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2 justify-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setSelectedCoupon(coupon);
                                setShowDetailModal(true);
                              }}
                              className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                              aria-label={t("admin.coupons.table.viewDetails")}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("admin.coupons.table.viewDetails")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleDeleteClick(coupon.id)}
                              className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                              aria-label={t("admin.coupons.table.deleteCoupon")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("admin.coupons.table.deleteCoupon")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </BrutalCard>

        {/* Create modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {t("admin.createNewCoupon")}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={createCoupon} className="space-y-4">
              <div className="space-y-6">
                {/* === SEZIONE 1: INFORMAZIONI BASE === */}
                <div>
                  <h3 className="font-bold text-lg mb-3">
                    {t("admin.coupons.createModal.sectionBasic")}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block mb-2 font-bold">
                        {t("admin.code")}
                      </label>
                      <BrutalInput
                        placeholder="SUMMER2025"
                        value={formData.code}
                        onChange={(e) =>
                          setFormData({ ...formData, code: e.target.value.toUpperCase() })
                        }
                      />
                      <p className="text-sm text-muted-foreground mt-1">{t("admin.coupons.createModal.codeOptional")}</p>
                    </div>

                    <div className="col-span-2">
                      <label className="block mb-2 font-bold">
                        {t("admin.description")}
                      </label>
                      <BrutalTextarea
                        placeholder={t("admin.couponDescription")}
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({ ...formData, description: e.target.value })
                        }
                        className="h-20"
                      />
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                {/* === SEZIONE 2: CONFIGURAZIONE SCONTO === */}
                <div>
                  <h3 className="font-bold text-lg mb-3">
                    {t("admin.coupons.createModal.sectionDiscount")}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 font-bold">
                        {t("admin.discountType")} *
                      </label>
                      <select
                        className="w-full border-[3px] border-dark p-3 bg-card"
                        value={formData.discount_type}
                        onChange={(e) =>
                          setFormData({ ...formData, discount_type: e.target.value })
                        }
                        required
                      >
                        <option value="percentage">{t("admin.percentage")}</option>
                        <option value="fixed">{t("admin.fixedAmount")}</option>
                        <option value="full">{t("admin.fullDiscount")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 font-bold">
                        {t("admin.discountValue")}
                      </label>
                      <BrutalInput
                        type="number"
                        placeholder="25"
                        value={formData.discount_value}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discount_value: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* === SEZIONE DURATA - Solo per subscription === */}
                {hasSubscriptionPlans() && (
                  <div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block mb-2 font-bold">
                          {t("admin.coupons.createModal.durationType")} *
                        </label>
                        <select
                          className="w-full border-[3px] border-dark p-3 bg-card"
                          value={formData.duration_type}
                          onChange={(e) =>
                            setFormData({ 
                              ...formData, 
                              duration_type: e.target.value,
                              duration_in_months: null
                            })
                          }
                          required
                        >
                          <option value="once">{t("admin.coupons.createModal.durationOnce")}</option>
                          <option value="forever">{t("admin.coupons.createModal.durationForever")}</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("admin.coupons.createModal.durationHint")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Separator className="my-4" />

                {/* === SEZIONE 3: APPLICABILITÀ === */}
                <div>
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    {t("admin.coupons.createModal.sectionApplicability")}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("admin.coupons.createModal.applicabilityHint")}
                  </p>
                  
                  {/* Quick select buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <BrutalButton 
                      type="button" 
                      variant={formData.applicable_plans.length === 0 ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setFormData({...formData, applicable_plans: []})}
                    >
                      {t("admin.coupons.applicability.all")}
                    </BrutalButton>
                    <BrutalButton 
                      type="button" 
                      variant={JSON.stringify([...formData.applicable_plans].sort()) === JSON.stringify(['base', 'plus', 'pro']) ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setFormData({...formData, applicable_plans: ['base', 'pro', 'plus']})}
                    >
                      {t("admin.coupons.applicability.plansOnly")}
                    </BrutalButton>
                    <BrutalButton 
                      type="button" 
                      variant={JSON.stringify([...formData.applicable_plans].sort()) === JSON.stringify(['social_boost', 'spotlight']) ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setFormData({...formData, applicable_plans: ['spotlight', 'social_boost']})}
                    >
                      {t("admin.coupons.applicability.visibilityOnly")}
                    </BrutalButton>
                    <BrutalButton 
                      type="button" 
                      variant={JSON.stringify([...formData.applicable_plans].sort()) === JSON.stringify(['job_posting_pack_3', 'job_posting_pack_5', 'job_posting_single']) ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setFormData({...formData, applicable_plans: ['job_posting_single', 'job_posting_pack_3', 'job_posting_pack_5']})}
                    >
                      {t("admin.coupons.applicability.jobCreditsOnly")}
                    </BrutalButton>
                  </div>

                  {/* Checkboxes for individual products */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { id: 'base', label: t("admin.coupons.plans.base"), color: 'bg-gray-100' },
                      { id: 'pro', label: t("admin.coupons.plans.pro"), color: 'bg-blue-50' },
                      { id: 'plus', label: t("admin.coupons.plans.plus"), color: 'bg-purple-50' },
                      { id: 'spotlight', label: t("admin.coupons.plans.spotlight"), color: 'bg-amber-50' },
                      { id: 'social_boost', label: t("admin.coupons.plans.socialBoost"), color: 'bg-violet-50' },
                      { id: 'job_posting_single', label: t("admin.coupons.plans.jobSingle"), color: 'bg-green-50' },
                      { id: 'job_posting_pack_3', label: t("admin.coupons.plans.jobPack3"), color: 'bg-green-100' },
                      { id: 'job_posting_pack_5', label: t("admin.coupons.plans.jobPack5"), color: 'bg-green-200' },
                    ].map((plan) => (
                      <label 
                        key={plan.id} 
                        className={`flex items-center gap-2 p-3 border-2 border-border rounded cursor-pointer hover:bg-muted/50 transition-colors ${plan.color}`}
                      >
                        <Checkbox
                          checked={formData.applicable_plans.length === 0 || formData.applicable_plans.includes(plan.id)}
                          onCheckedChange={(checked) => {
                            const allPlans = ['base', 'pro', 'plus', 'spotlight', 'social_boost', 'job_posting_single', 'job_posting_pack_3', 'job_posting_pack_5'];
                            if (formData.applicable_plans.length === 0) {
                              // If "all" was selected, switch to specific selection
                              if (!checked) {
                                setFormData({
                                  ...formData, 
                                  applicable_plans: allPlans.filter(p => p !== plan.id)
                                });
                              }
                            } else {
                              if (checked) {
                                const newPlans = [...formData.applicable_plans, plan.id];
                                // If all 8 selected, reset to empty (= all)
                                if (newPlans.length === 8) {
                                  setFormData({...formData, applicable_plans: []});
                                } else {
                                  setFormData({...formData, applicable_plans: newPlans});
                                }
                              } else {
                                setFormData({
                                  ...formData, 
                                  applicable_plans: formData.applicable_plans.filter(p => p !== plan.id)
                                });
                              }
                            }
                          }}
                        />
                        <span className="font-medium text-sm">{plan.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Separator className="my-4" />

                {/* === SEZIONE 4: VALIDITÀ E LIMITI === */}
                <div>
                  <h3 className="font-bold text-lg mb-3">
                    {t("admin.coupons.createModal.sectionValidity")}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block mb-2 font-bold">
                        {t("admin.maxUses")}
                      </label>
                      <BrutalInput
                        type="number"
                        placeholder={t("admin.unlimited")}
                        value={formData.max_uses || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            max_uses: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      />
                      <p className="text-xs text-muted-foreground mt-1">Vuoto = illimitato</p>
                    </div>

                    <div>
                      <label className="block mb-2 font-bold">
                        {t("admin.validFrom")} *
                      </label>
                      <BrutalInput
                        type="datetime-local"
                        value={formData.valid_from}
                        onChange={(e) =>
                          setFormData({ ...formData, valid_from: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-bold">
                        {t("admin.validUntil")} *
                      </label>
                      <BrutalInput
                        type="datetime-local"
                        value={formData.valid_until}
                        onChange={(e) =>
                          setFormData({ ...formData, valid_until: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <BrutalButton
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t("common.cancel")}
                </BrutalButton>
                <BrutalButton type="submit">
                  {t("admin.createCoupon")}
                </BrutalButton>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail modal */}
      {selectedCoupon && (
        <CouponDetailModal
          coupon={selectedCoupon}
          open={showDetailModal}
          onOpenChange={(open) => {
            setShowDetailModal(open);
            if (!open) {
              setSelectedCoupon(null);
            }
          }}
          onUpdate={loadCoupons}
        />
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322] w-[90vw] sm:w-auto sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">{t("admin.coupons.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.coupons.deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <BrutalButton variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton variant="danger" onClick={confirmDelete}>
              {t("common.delete")}
            </BrutalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminLayout>
  );
}
